import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { MonthPicker } from '@/components/MonthPicker';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTh } from '@/components/SortableTh';
import { useAuthStore, hasPermission } from '@/stores/auth';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * v0.5 AIメニュー開発の利用ランキング (D-976〜D-985)。
 * 設計 (正典): docs/design/v05-ai-menu-usage-ranking-detail.md §5
 *
 * 既存コンフィグの「AIメニュー開発の活用状況」セクション (旧 AiMenuAdoptionSection) を置き換える
 * 独立画面。権限保有者全員 (0件含む) + 期間中に使ったが今は権限が無い人をランキング表示する。
 */

export type RankingRow = {
  userId: string;
  displayName: string | null;
  hasPermission: boolean;
  tokens: number;
  requestCount: number;
  costUsd: number;
  lastUsedAt: string | null;
  nodeId: string | null;
};

type RankingResponse = {
  startMonth: string;
  endMonth: string;
  nodeId: string | null;
  /** own_store 相当のスコープでは null (他人の利用は見せない、D-984) */
  rows: RankingRow[] | null;
  totalHolderCount: number;
  formerHolderCount: number;
  outOfScopeHolderCount: number;
  totalMatchedCount: number;
  truncated: boolean;
};

const currentYearMonth = (): string => {
  const now = new Date();
  const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return `${jst.getUTCFullYear()}-${String(jst.getUTCMonth() + 1).padStart(2, '0')}`;
};

/**
 * 以下、テスト容易性のために切り出した純粋関数群 (test-runner 実装後レビュー MAJOR 対応)。
 * DOM 環境 (jsdom 等) は未導入のため (apps/web/src/pages/__tests__/ImportsPage.test.ts と同じ事情)、
 * コンポーネント本体から取り出したロジックを直接テストする。
 */

/** 既定ソート (D-982): 利用回数の降順。 */
export const RANKING_DEFAULT_SORT: { key: string; dir: 'asc' | 'desc' } = { key: 'requestCount', dir: 'desc' };

/**
 * useTableSort に渡す accessors。`nodeNameById` はコンポーネント側の useMemo で構築される
 * 参照 (組織ツリー由来) を受け取る関数注入にして、DOM/ツリーデータ無しでもテストできるようにする。
 */
export const buildRankingSortAccessors = (
  nodeNameById: (id: string) => string,
): Record<string, (r: RankingRow) => unknown> => ({
  name: (r: RankingRow) => r.displayName ?? '',
  node: (r: RankingRow) => (r.nodeId ? nodeNameById(r.nodeId) : ''),
  // hasPermission を true→1/false→0 として他の数値列と同じ昇順/降順規則に従わせる (design §5-2)
  hasPermission: (r: RankingRow) => (r.hasPermission ? 1 : 0),
  requestCount: (r: RankingRow) => r.requestCount,
  tokens: (r: RankingRow) => r.tokens,
  costUsd: (r: RankingRow) => r.costUsd,
  lastUsedAt: (r: RankingRow) => r.lastUsedAt ?? '',
});

/** 利用回数セルの表示文字列。0件も「0回」と表示する (D-979、「利用していません」は使わない)。 */
export const formatRequestCount = (n: number): string => `${n}回`;

/** トークン数セルの表示文字列。0件も「0」と表示する (空欄/ハイフンにしない)。 */
export const formatTokenCount = (n: number): string => n.toLocaleString();

/** truncated 時の「◯件中◯件を表示しています」注記文言。 */
export const formatTruncatedNotice = (totalMatchedCount: number, shownCount: number): string =>
  `${totalMatchedCount}件中 ${shownCount}件を表示しています。期間や対象ノードを絞り込んでください。`;

/**
 * 「操作」列のユーザー編集リンク先。`user:view` を持たない閲覧者には /users 自体が開けず
 * dead-end になるため、リンクにせずプレーンテキスト表示にする (QA レビュー Minor、design §5-2)。
 */
export const resolveEditLinkPath = (userId: string, canOpenUsers: boolean): string | null =>
  canOpenUsers ? `/users?edit_user=${userId}` : null;

/**
 * フィルタ (start/end/node_id) 変更時の次の URLSearchParams を組み立てる。値が空文字なら
 * そのキーを削除する (「全体」選択時に node_id を残さない等)。呼び出し元は
 * `setSearchParams(next, { replace: true })` で履歴を汚さず適用する (design §5-2 MEDIUM)。
 */
export const nextRankingSearchParams = (
  current: URLSearchParams,
  updates: Record<string, string>,
): URLSearchParams => {
  const next = new URLSearchParams(current);
  for (const [key, value] of Object.entries(updates)) {
    if (value) next.set(key, value); else next.delete(key);
  }
  return next;
};

export function AiMenuUsageRankingPage() {
  const currentUser = useAuthStore((s) => s.user);
  // フィルタ状態は useSearchParams で URL クエリに保持する (design §5-2 H3): D-981 のユーザー編集
  // リンクで /users へ離脱した後、ブラウザの「戻る」で同じ絞り込み状態へ復帰させるため。
  const [searchParams, setSearchParams] = useSearchParams();
  const startMonth = searchParams.get('start') || currentYearMonth();
  const endMonth = searchParams.get('end') || currentYearMonth();
  const nodeId = searchParams.get('node_id') || '';

  // フィルタ変更時は常に現在の URL を置き換える (design §5-2): 履歴を積むと「戻る」の挙動が不定になる。
  const updateParams = (updates: Record<string, string>) => {
    setSearchParams((prev) => nextRankingSearchParams(prev, updates), { replace: true });
  };

  const { data: nodesData } = useQuery<NodesResponse>({
    queryKey: ['nodes'],
    queryFn: () => apiFetch<NodesResponse>('/nodes'),
    staleTime: 5 * 60 * 1000,
  });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  const nodeNameById = useMemo(() => {
    const map = new Map(flatNodes.map((n) => [n.node.node_id, n.node.name]));
    return (id: string) => map.get(id) ?? `(名称を取得できません: ${id})`;
  }, [flatNodes]);

  const { data, isLoading, isError, error } = useQuery<RankingResponse>({
    queryKey: ['ai-menu-usage-ranking', startMonth, endMonth, nodeId],
    queryFn: () => apiFetch<RankingResponse>(
      `/ai-usage/menu-ranking?start=${startMonth}&end=${endMonth}${nodeId ? `&node_id=${nodeId}` : ''}`,
    ),
  });

  const rows = data?.rows ?? [];
  // user:view が無い閲覧者には /users 自体が開けず dead-end になるため、リンクではなく
  // プレーンテキストで氏名を表示する (design §5-2、QA レビュー Minor)。
  const canOpenUsers = hasPermission(currentUser?.effective_permissions, 'user', 'view', currentUser?.non_inheriting_features);

  const { sortKey, sortDir, toggleSort, sortedRows } = useTableSort<RankingRow>(rows as unknown as Record<string, unknown>[] & RankingRow[], {
    defaultKey: RANKING_DEFAULT_SORT.key,
    defaultDir: RANKING_DEFAULT_SORT.dir,
    accessors: buildRankingSortAccessors(nodeNameById) as unknown as Record<string, (r: RankingRow) => unknown>,
  });

  const periodActiveCount = rows.filter((r) => r.hasPermission && r.requestCount > 0).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h1">AIメニュー利用状況</h1>
        <p className="text-sm text-neutral-sub mt-1">
          AIメニュー開発を使える権限を持つ方ごとの利用状況をランキングで確認します
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label htmlFor="ranking-start-month" className="block text-xs text-text-sub mb-1">開始月</label>
          <MonthPicker id="ranking-start-month" aria-label="開始月" value={startMonth}
            onChange={(v) => updateParams({ start: v })}
            className="px-3 py-2 rounded border border-surface-line focus-ring text-sm" />
        </div>
        <div>
          <label htmlFor="ranking-end-month" className="block text-xs text-text-sub mb-1">終了月</label>
          <MonthPicker id="ranking-end-month" aria-label="終了月" align="right" value={endMonth}
            onChange={(v) => updateParams({ end: v })}
            className="px-3 py-2 rounded border border-surface-line focus-ring text-sm" />
        </div>
        <div className="min-w-[220px]">
          <span className="block text-xs text-text-sub mb-1" id="ranking-node-label">対象ノード</span>
          <Select value={nodeId || 'all'} onValueChange={(v) => updateParams({ node_id: v === 'all' ? '' : v })}>
            <SelectTrigger aria-labelledby="ranking-node-label">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全体 (テナント合計)</SelectItem>
              {flatNodes.map(({ node, depth }) => (
                <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading && <p className="text-sm text-text-sub">読み込み中…</p>}
      {isError && <div className="p-3 rounded bg-warning-bg text-warning-text border border-warning-border">{(error as Error).message}</div>}

      {!isLoading && !isError && data && data.rows !== null && (
        <>
          <div className="space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="border border-surface-line rounded-lg bg-surface-card p-4">
                <div className="text-xs text-text-sub mb-1">権限保有者</div>
                <div className="text-2xl font-bold tabular-nums">{data.totalHolderCount}名</div>
              </div>
              <div className="border border-surface-line rounded-lg bg-surface-card p-4">
                <div className="text-xs text-text-sub mb-1">期間内に利用</div>
                <div className="text-2xl font-bold tabular-nums">{periodActiveCount}名</div>
              </div>
              <div className="border border-surface-line rounded-lg bg-surface-card p-4">
                <div className="text-xs text-text-sub mb-1">元保有者 (今は権限なし)</div>
                <div className="text-2xl font-bold tabular-nums">{data.formerHolderCount}名</div>
              </div>
            </div>
            {/* ⚠️ 断罪的に見えないための注記 (design §5-2、既存 AiMenuAdoptionSection からの引き継ぎ) */}
            <p className="text-xs text-text-sub">
              長期休業・繁忙期など、使っていないことに正当な理由がある場合があります。権限の見直しが必要な場合は
              <Link to="/settings/roles" className="underline underline-offset-2 mx-1 text-chrome-base">ロール管理</Link>
              から行ってください。
            </p>
            {/* 2026-09-24 user指摘: 「利用回数」が新規メニュー開発の実行回数と誤読されやすいための注記 */}
            <p className="text-xs text-text-sub">
              「利用回数」は新規メニュー開発を実行した回数ではなく、AIメニュー開発内でのメッセージ送信・再生成などAIとのやり取りの回数です。1回の開発で複数回やり取りすれば、その分だけ回数は増えます。
            </p>
            {data.outOfScopeHolderCount > 0 && (
              <p className="text-xs text-text-sub">
                絞り込み中の組織の配下に主所属を持たない権限保有者が {data.outOfScopeHolderCount}名います。
              </p>
            )}
          </div>

          <div className="border border-surface-line rounded-lg bg-surface-card p-4 overflow-x-auto">
            {sortedRows.length === 0 ? (
              <p className="text-sm text-text-sub">対象データがありません</p>
            ) : (
              <table className="w-full text-sm min-w-[880px]">
                <thead>
                  <tr className="border-b border-surface-line text-left text-text-sub">
                    <SortableTh sortKey="name" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>氏名</SortableTh>
                    <SortableTh sortKey="node" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>所属</SortableTh>
                    <SortableTh sortKey="hasPermission" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>権限</SortableTh>
                    <SortableTh sortKey="requestCount" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right">利用回数</SortableTh>
                    <SortableTh sortKey="tokens" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right">トークン数</SortableTh>
                    <SortableTh sortKey="costUsd" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right">費用(USD)</SortableTh>
                    <SortableTh sortKey="lastUsedAt" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>最終利用日</SortableTh>
                    <th scope="col" className="px-3 py-2 font-medium">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((r) => (
                    <tr key={r.userId} className="border-b border-surface-line last:border-0">
                      <td className="px-3 py-2">{r.displayName ?? r.userId}</td>
                      <td className="px-3 py-2">{r.nodeId ? nodeNameById(r.nodeId) : '所属なし'}</td>
                      <td className="px-3 py-2">
                        {/* 権限バッジは neutral トークン (赤系不使用)。「なし」= 悪い、という断罪的な色付けを避ける */}
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-surface-sunken text-text-sub">
                          {r.hasPermission ? '保有' : 'なし'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatRequestCount(r.requestCount)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatTokenCount(r.tokens)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">${r.costUsd.toFixed(2)}</td>
                      <td className="px-3 py-2 text-text-sub">
                        {r.lastUsedAt ? new Date(r.lastUsedAt).toLocaleDateString('ja-JP') : '利用記録なし'}
                      </td>
                      <td className="px-3 py-2">
                        {(() => {
                          const editLinkPath = resolveEditLinkPath(r.userId, canOpenUsers);
                          return editLinkPath ? (
                            <Link to={editLinkPath} className="underline underline-offset-2 text-chrome-base">
                              {r.displayName ?? r.userId}
                            </Link>
                          ) : (
                            <span>{r.displayName ?? r.userId}</span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {data.truncated && (
              <p className="text-xs text-text-sub mt-2">
                {formatTruncatedNotice(data.totalMatchedCount, rows.length)}
              </p>
            )}
          </div>

          <p className="text-sm text-text-sub">
            利用上限の設定は
            <Link to="/settings/ai" className="underline underline-offset-2 mx-1 text-chrome-base">コンフィグ</Link>
            から行えます。
          </p>
        </>
      )}
    </div>
  );
}
